/**
 * FormBodyContainer.js
 * Created by Vijay on 05/15/2020.
 */

import React, {useEffect, useContext, useState, createContext, useCallback, Fragment, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';
import {labels} from '../../../../util/utils';
import env from '../../../../util/env';
import Utility from '../../../../util/utilityfunctions';
import {DocumentTypeIdContext} from '../../../Main/Forms/CanvasFormContainer';
import {
	serviceURL,
	formBodyType,
	clientSideEventNames,
	validationTypes,
	validationFilter,
	documentTypes,
	formRoutes,
	actionType
} from '../../../../util/uiconstants';
import {currentKendoValue} from '../../../../util/utils';
import {loadStorage, saveStorage, clearItem} from '../../localStorage';
import Loader from '../../LoadingIndicator/LoadingIndicator';
import Modal from '../../Modal/Modal';
import TabBar from '../../Tabs/TabBar';
import Checkbox from '../../BaseComponentWrappers/MotifCheckbox';
import {createRequestURL, createUpdatePayloadForRichText} from './Common/BodyResponseUtility';
import {
	updateFormBodyResponse,
	getFormBodiesBySectionId,
	updateStoreForFormDataNoSaga,
	updateFormBodyResponseNoSaga,
	getFormBodiesAfterProcessingResponse,
	updateFormDataAfterBodyResponse
} from '../../../../actions/FormData/formdataactions';
import {setIsModalOpen} from '../../../../actions/setismodalopen';
import {updateStorybook} from '../../../../actions/Task/taskDocumentActions';
import {clearFormBodySaveState, clearFormBodySaveStateById} from '../../../../actions/Form/formBodyActions';
import {getValidationsForDocumentId} from '../../../../selectors/Forms/canvasFormSelectors';
import {getUnresolvedCommentsCount} from '../../../../actions/ValidationControl/validationcontrolactions';
import {
	bodyHasUnsavedChanges,
	getFormBodyById,
	getFormBodyDataBySectionId
} from '../../../../selectors/Forms/formBodySelectors';
import CommentInputModal from '../../EngagementComment/CommentInputModal';
import FormBodyChangesWatcher from './FormBodyChangesWatcher';
import GuidanceModalContext from '../../CanvasForm/Bodies/Common/GuidanceModalContext';
import DraggableGuidance from '../../CanvasForm/Bodies/Common/DraggableGuidance';
import {cloneDeep} from 'lodash';
import {processBodyResponses} from '../../containerFunctions';
import FormBodyItem from '../Bodies/formBodyItem';
import {useDocumentId, useEngagementId, useUserPreferences} from '../../../../util/customHooks';
import {getTaskDocumentByDocumentId, getTasksByTaskIdList} from '../../../../selectors/Ares/selectors';

export const FormBodyTypeIdContext = createContext(undefined);

let bodyId = undefined;

export default function FormBodyContainer(props) {
	const {
		bodyOnlyView,
		sectionExpanded,
		sectionId,
		headerId,
		selectedSectionId,
		snapShotbodies,
		sectionRef,
		forceReload,
		isSnapshotView,
		scroll,
		setScroll
	} = props;

	let [bodies, setBodies] = useState([]),
		[showConflictModal, setShowConflictModal] = useState(false),
		[conflictText, setConflictText] = useState(undefined),
		[saveConflict, setSaveConflict] = useState(false),
		[activeConflictTab, setActiveConflictTab] = useState(undefined),
		[conflictShowChangesChecked, setConflictShowChangesChecked] = useState(false),
		[saveComplete, setSaveComplete] = useState(false),
		[showEditTextResponse, setShowEditTextResponse] = useState(false),
		[bodyDataLoaded, setBodyDataLoaded] = useState(false);

	const [guidanceModalContents, setGuidanceModalContents] = useState({
		bodyId: undefined,
		data: []
	});

	const showGuidanceModal = guidanceModalContents.data.length > 0;

	const dispatch = useDispatch();

	const validationsFilter = loadStorage('validationsFilter') || 0;

	const engagementId = useEngagementId(),
		userPreferences = useUserPreferences(),
		documentId = useDocumentId(),
		documentTypeId = useContext(DocumentTypeIdContext);
	const canvasFormDocument = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));

	const selectedHeaderIdKey = useMemo(() => `selectedHeaderId-${documentId}`, [documentId]);

	const formBodyData = useSelector((store) => {
			return !props.isSnapshotView ? getFormBodyDataBySectionId(store, sectionId) : {};
		}),
		formBodySaveData = useSelector((store) => {
			if (bodyId) {
				return getFormBodyById(store, bodyId);
			}
		}),
		formBodyDisplayData = useSelector((store) => {
			return !props.isSnapshotView ? store.formBodyDisplayData : {};
		}),
		formBodyDisplayDataForUpdating = useSelector((store) => {
			return !props.isSnapshotView ? store.formBodyDisplayDataForUpdating : {};
		}),
		validations = useSelector((store) => {
			return !props.isSnapshotView ? getValidationsForDocumentId(store, documentId) : {};
		}),
		formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		quickLinks = useSelector((store) => store.quickLink),
		canvasFormTasksList = useSelector((state) =>
			getTasksByTaskIdList(
				state,
				canvasFormDocument?.tasks?.map((taskId) => taskId)
			)
		);

	const bodyIdPrefix = `section-${sectionId}-body-`;

	const documentRelatedTasks = useMemo(() => {
		const ids = [];

		if (Array.isArray(canvasFormTasksList) && canvasFormTasksList.length > 0) {
			ids.push(...canvasFormTasksList.map((task) => ({taskId: task.taskId, taskTypeId: task.taskTypeId})));
		}

		return ids;
	}, [canvasFormDocument]);

	const getBodiesBySectionId = (mounted) => {
		if (sectionId) {
			setBodyDataLoaded(false);

			dispatch(
				getFormBodiesBySectionId(
					env.getBaseURL(serviceURL, engagementId) +
						`forms/${documentId}/headers/${headerId}/sections/${sectionId}/Bodies?includeAction=true`
				)
			)
				.then(() => {
					setBodyDataLoaded(true);
				})
				.catch(() => {
					setBodyDataLoaded(true);
				});
		}
	};

	const isFormBodyDisabled = (formBody) => {
		let isDisabled = false;

		const bodySelected = formBody.optionsAndResponses?.some((opt) => opt.bodySelectedOptionId === opt.bodyOptionId);
		const permissions = userPreferences.permissions;
		if (documentTypeId === documentTypes.INDEPENDENCEFORMTEMPLATE) {
			isDisabled = permissions === null || permissions === undefined || !permissions.canUpdateEngagementContent;
			//If the document is an independence view template, we need to disable any formbody which holds an action of typeId "IndependenceException" (1)
			if (!isDisabled && formBody.actions && formBody.actions.length > 0) {
				isDisabled = formBody.actions.some((action) => {
					return (
						(action.actionTypeId === actionType.IndependenceException ||
							action.actionTypeId === actionType.PersonalIndependenceResponse) &&
						!bodySelected
					);
				});
			}
		}

		if (
			documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL &&
			userPreferences?.id !== formData?.independenceInformation?.userId
		) {
			return true;
		}

		if (documentTypeId === documentTypes.PROFILEV2) {
			return permissions && !permissions.canUpdateEngagementContent;
		}

		return isDisabled;
	};

	const updateBodyOptions = (bodyId, selectedId, url, payload) => {
		saveStorage('bodyId', bodyId);
		saveStorage('selectedId', selectedId);
		Utility.setStoredPayload(bodyId, payload);
		//saveStorage('payload', payload);
		saveStorage(selectedHeaderIdKey, headerId);
		setSaveConflict(true);
		setSaveComplete(false);

		if (payload.bodyTypeId === formBodyType.InlineRichText || payload.bodyTypeId === formBodyType.TextResponse) {
			return dispatch(updateFormBodyResponseNoSaga(url, payload)).finally(() => {
				setSaveConflict(false);
				setSaveComplete(true);
				setShowConflictModal(false);
			});
		} else {
			return dispatch(updateFormBodyResponse(url, payload))
				.catch(() => {
					document.getElementById(bodyId).classList.remove('greyOutArea');
				})
				.finally(() => {
					setSaveConflict(false);
					setSaveComplete(true);
					setShowConflictModal(false);
				});
		}
	};

	const updateFormBodyStore = (serverText, concurrencyToken, data) => {
		bodyId = data.id;
		let payloadData = Utility.getStoredPayload(data.id);
		if (!payloadData) {
			payloadData = {bodyResponseObject: [{bodyFreeText: ''}], bodyTypeId: data.bodyTypeId};
		}
		if (serverText) {
			payloadData.bodyResponseObject[0].bodyFreeText = serverText;
			payloadData.concurrencyToken = concurrencyToken;
			/*dispatch(
				updateBodyFreeText(formBodyDisplayData.id, serverText, formBodyDisplayData.bodyTypeId, concurrencyToken, false)
			);*/
			Utility.setStoredPayload(data.id, payloadData);
			setShowConflictModal(false);
			dispatch(
				updateStoreForFormDataNoSaga({
					id: data.id,
					bodyFreeText: serverText,
					concurrencyToken: concurrencyToken,
					isConflict: false
				})
			).finally(() => {
				clearItem('selectedId');
				setSaveComplete(true);
				currentKendoValue[data.id] = serverText;
			});
		} else {
			if (
				payloadData.bodyTypeId === formBodyType.InlineRichText ||
				payloadData.bodyTypeId === formBodyType.TextResponse
			) {
				dispatch(updateStoreForFormDataNoSaga(data)).finally(() => {
					clearItem('selectedId');
					setSaveComplete(true);
					currentKendoValue[data.id] = data.bodyFreeText;
				});
			} else {
				processBodyResponse(
					data,
					payloadData.bodyResponseObject.filter((d) => d.isSelected === true).map((d) => d.bodyOptionId) || [],
					payloadData.bodyTypeId,
					formBodyData,
					payloadData.bodyResponseObject,
					payloadData.concurrencyToken
				);
				clearItem('selectedId');
				Utility.removedStoredPayload(formBodyDisplayData.id);
				setSaveComplete(true);
				currentKendoValue[data.id] = data.bodyFreeText;
			}
		}
	};

	const findBodyInValidations = (bodyId, validations) => {
		let found = false;
		function recursivelyFindBodyInValidations(bodyId, validations) {
			for (let i = 0; i < validations.length; i++) {
				if (
					Number(bodyId) === Number(validations[i].validationId) &&
					((validationsFilter === validationFilter.INCOMPLETE &&
						Number(validations[i].validationTypeId) !== validationTypes.CanvasFromComments) ||
						(validationsFilter === validationFilter.COMMENTS &&
							Number(validations[i].validationTypeId) === validationTypes.CanvasFromComments))
				) {
					found = true;
				} else {
					if (validations[i].children && validations[i].children.length > 0) {
						recursivelyFindBodyInValidations(bodyId, validations[i].children);
						if (found) {
							break;
						}
					}
				}
			}
		}

		recursivelyFindBodyInValidations(bodyId, validations);
		return found;
	};

	const conflictTabChange = (tab) => {
		setActiveConflictTab(tab);
		setConflictShowChangesChecked(false);
		if (tab === 'current') {
			setConflictText(formBodyDisplayData.currentBodyFreeText ? formBodyDisplayData.currentBodyFreeText : '');
		} else {
			setConflictText(formBodyDisplayData.currentDBBodyFreeText ? formBodyDisplayData.currentDBBodyFreeText : '');
		}
	};

	const showConflictChanges = (event) => {
		const displayData = formBodyDisplayData;
		if (event.currentTarget.checked === true) {
			setConflictShowChangesChecked(true);
			setConflictText(displayData.comparedText);
		} else {
			setConflictShowChangesChecked(false);
			setActiveConflictTab('current');
			setConflictText(displayData.currentBodyFreeText);
		}
	};

	const conflictClose = () => {
		setConflictShowChangesChecked(false);
		setShowConflictModal(false);
		setShowEditTextResponse(true);
	};

	const handleConflict = () => {
		setShowEditTextResponse(false);
		if (activeConflictTab === 'server') {
			/*update store with server response*/
			updateFormBodyStore(
				formBodyDisplayData.currentDBBodyFreeText,
				formBodyDisplayData.concurrencyToken,
				formBodyDisplayData
			);
			window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
		} else if (activeConflictTab === 'current') {
			/*save current text to db*/
			let payload = createUpdatePayloadForRichText(
				formBodyDisplayData.id,
				formBodyDisplayData.bodyTypeId,
				formBodyDisplayData.currentBodyFreeText,
				formBodyDisplayData.concurrencyToken
			);
			updateBodyOptions(
				formBodyDisplayData.id,
				selectedSectionId,
				createRequestURL(engagementId, documentId, headerId, sectionId, formBodyDisplayData.id, documentTypeId),
				payload
			).then(() => {
				updateFormBodyStore(undefined, formBodyDisplayData.concurrencyToken, {
					id: formBodyDisplayData.id,
					bodyFreeText: formBodyDisplayData.currentBodyFreeText,
					concurrencyToken: formBodyDisplayData.concurrencyToken,
					isConflict: false
				});
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getValidations));
			});
		}
	};

	const filterForValidations = () => {
		if (formBodyData && formBodyData.length > 0) {
			let myBodies = formBodyData;
			if (myBodies && myBodies.length > 0) {
				if (validationsFilter !== validationFilter.NONE) {
					let validationBodies = [];
					if (validations && validations.length > 0) {
						/*find the validations for this body and show only incomplete*/
						myBodies.forEach((body) => {
							if (findBodyInValidations(body.id, validations)) {
								validationBodies.push(body);
							}
						});
						setBodies(validationBodies);
						setBodyDataLoaded(true);
					}
					if (validationBodies.length === 0) {
						window.dispatchEvent(new CustomEvent(clientSideEventNames.noIncompleteBodies, {}));
					}
				} else {
					setBodies(myBodies);
					setBodyDataLoaded(true);
				}
			} else {
				setBodies([]);
				setBodyDataLoaded(true);
			}
		}
	};

	const processBodyResponse = (
		formBodyDisplayData,
		selectedBodyOptions,
		bodyTypeId,
		formBodyData,
		bodySelectedOption,
		concurrencyToken
	) => {
		if (formBodyDisplayData && formBodyDisplayData.id) {
			const result = processBodyResponses(
				formBodyDisplayData,
				cloneDeep(formBodyData),
				cloneDeep(formData),
				selectedBodyOptions,
				bodyTypeId,
				bodySelectedOption,
				concurrencyToken,
				false
			);
			if (result) {
				dispatch(getFormBodiesAfterProcessingResponse(result.formBodyData, sectionId));
				dispatch(updateFormDataAfterBodyResponse(result.formData));
				dispatch(updateStorybook(result.formData));
			}
		}
	};

	const checkConflict = (data) => {
		if (data?.isConflict !== undefined && data?.isConflict !== null && data?.isConflict === true) {
			if (data?.discardChanges === true) {
				updateFormBodyStore(data?.currentDBBodyFreeText, data?.concurrencyToken, data);
			} else {
				dispatch(setIsModalOpen(true));
				setShowConflictModal(true);
				setActiveConflictTab('current');
				setConflictText(data?.currentBodyFreeText);
			}
		}
		updateFormBodyStore(undefined, undefined, data);
	};

	// Executed when the user clicks on "Leave" button in the unsaved changes modal
	const handleAfterNavigationConfirm = useCallback(() => {
		// Close comments modal (???)
		dispatchEvent(new CustomEvent(clientSideEventNames.clearKendoEditorContent));
		dispatch(clearFormBodySaveState());
	}, [dispatch]);

	useEffect(() => {
		filterForValidations();
	}, [validations]);

	useEffect(() => {
		let myBodies = formBodyData;
		if (myBodies && myBodies.length > 0) {
			setBodies(myBodies);
			if (documentTypeId === documentTypes.INDEPENDENCEFORMINDIVIDUAL && quickLinks?.independenceSummaryDocId) {
				dispatch(
					getUnresolvedCommentsCount(engagementId, documentId, documentTypeId, null, null, [
						quickLinks.independenceSummaryDocId,
						documentId
					])
				);
			} else {
				dispatch(getUnresolvedCommentsCount(engagementId, documentId, documentTypeId));
			}
		} else {
			if (!isSnapshotView) {
				setBodies([]);
			}
		}
		filterForValidations();
	}, [formBodyData]);

	useEffect(() => {
		let mounted = true;
		if (sectionExpanded && sectionId === selectedSectionId) {
			if (!isSnapshotView) {
				getBodiesBySectionId(mounted);
			} else {
				setBodyDataLoaded(true);
				setBodies(snapShotbodies);
			}
		}
		return () => (mounted = false);
	}, [sectionId]);

	useEffect(() => {
		if (formBodyDisplayData && formBodyDisplayData.id) {
			checkConflict(formBodyDisplayData);
		}
	}, [formBodyDisplayData]);

	useEffect(() => {
		if (formBodyData && formBodyData.length > 0) {
			let myBodies = JSON.parse(JSON.stringify(formBodyData)).filter((bodies) => {
				return bodies.sectionId === sectionId;
			});
			if (myBodies && myBodies.length > 0) {
				if (validationsFilter !== validationFilter.NONE) {
					let incompleteBodies = [];
					if (validations && validations.length > 0) {
						/*find the validations for this body and show only incomplete*/
						myBodies.forEach((body) => {
							if (findBodyInValidations(body.id, validations)) {
								incompleteBodies.push(body);
							}
						});
						setBodies(incompleteBodies);
					}
					if (incompleteBodies.length === 0) {
						window.dispatchEvent(new CustomEvent(clientSideEventNames.noIncompleteBodies, {}));
					}
				} else {
					if (!isSnapshotView) {
						setBodies(myBodies.bodies);
					} else {
						setBodies(snapShotbodies);
					}
				}
			} else {
				setBodies([]);
			}
		}
	}, [forceReload]);

	useEffect(() => {
		if (!bodyOnlyView && scroll === true && props.match.url !== formRoutes.snapshot) {
			if (sectionRef.current) {
				sectionRef.current.scrollIntoView({
					behavior: 'smooth'
				});
			}
			setTimeout(() => {
				/*have to stop the scrolling for saving bodies*/
				setScroll(false);
			}, 1000);
		}
	});

	useEffect(() => {
		if (formBodyDisplayDataForUpdating && formBodyDisplayDataForUpdating.id) {
			updateFormBodyStore(undefined, undefined, formBodyDisplayDataForUpdating);
		}
	}, [formBodyDisplayDataForUpdating]);

	useEffect(() => {
		if (formBodySaveData && Utility.getStoredPayload(bodyId)) {
			const bodyHasChanges = bodyHasUnsavedChanges(formBodySaveData);
			if (bodyHasChanges) {
				dispatch(clearFormBodySaveStateById(bodyId));
			}
		}
	}, [formBodySaveData, dispatch]);

	const onGuidanceModalCloseHandler = useCallback(() => {
		setGuidanceModalContents({
			bodyId: undefined,
			data: []
		});
	}, []);

	const formBodyItem = (bodyElementId, body) => {
		return (
			<FormBodyItem
				bodyElementId={bodyElementId}
				headerId={headerId}
				sectionId={sectionId}
				body={body}
				documentId={documentId}
				isSnapshotView={isSnapshotView}
				isDisabled={isFormBodyDisabled(body)}
				getBodiesBySectionId={getBodiesBySectionId}
				showEditTextResponse={showEditTextResponse}
				setShowEditTextResponse={setShowEditTextResponse}
				saveComplete={saveComplete}
				updateBodyOptions={updateBodyOptions}
				validations={validations}
				documentRelatedTasks={documentRelatedTasks}
				formData={formData}
			/>
		);
	};

	return (
		<Wrapper
			className={`FormBodyContainer ${bodies &&
				bodies !== null &&
				((bodies.length > 0 && bodies.filter((d) => d.isDisplayed === true || d.isCustom).length === 0) ||
					bodies.length === 0) &&
				'FormBodyEmptyContainer'}`}
		>
			<section className="section-body">
				{!bodyDataLoaded ? (
					<Loader show view="inlineView" styleName="FormBodyLoader" />
				) : (
					<FormBodyChangesWatcher afterNavigationConfirm={handleAfterNavigationConfirm}>
						<GuidanceModalContext.Provider value={{guidanceModalContents, setGuidanceModalContents}}>
							{bodies &&
							bodies !== null &&
							((bodies.length > 0 && bodies.filter((d) => d.isDisplayed === true || d.isCustom).length === 0) ||
								bodies.length === 0) ? (
								formBodyData && formBodyData.length > 0 ? (
									<Loader show view="inlineView" styleName="FormBodyLoader" />
								) : (
									<section key="empty" className="emptyContainer">
										{labels.noBodies}
									</section>
								)
							) : (
								<>
									<section className="section-container">
										{bodies &&
											bodies.length > 0 &&
											bodies
												.filter((d) => d.isDisplayed === true)
												.map((body) => {
													const bodyElementId = `${bodyIdPrefix}${body.id}`;
													return (
														<Fragment key={bodyElementId}>
															<FormBodyTypeIdContext.Provider value={body?.bodyTypeId}>
																{body?.bodyTypeId !== formBodyType.NotDisplayedPreselectedOptions &&
																	formBodyItem(bodyElementId, body)}
															</FormBodyTypeIdContext.Provider>
														</Fragment>
													);
												})}
									</section>
									{showGuidanceModal && (
										<DraggableGuidance
											guidanceDetails={guidanceModalContents.data}
											show={showGuidanceModal}
											onModalCloseHandler={onGuidanceModalCloseHandler}
										/>
									)}
									<Modal
										modalonmodal
										show={showConflictModal}
										onHide={conflictClose}
										onOkClick={handleConflict}
										title={labels.confirmUpdate}
										showButtons
										closeBtnTitle={labels.returnToEditMode}
										confirmBtnTitle={labels.keepVersion}
										okTitle={conflictShowChangesChecked === true ? labels.uncheckTrackChangesToSave : ''}
										modalsize={'medium'}
										closeTitle={labels.returnToEditMode}
										isOkButtonDisabled={conflictShowChangesChecked === true}
										modalContainerClass="conflictDescriptionModal"
										modalbodystyle="conflictDescriptionModalBody customScrollbar"
									>
										<>
											<Loader show={saveConflict} />
											<section className="conflictDescriptionLabel">
												{labels.conflictDescription &&
													labels.conflictDescription.replaceAll('{0}', formBodyDisplayData?.modifiedUser).replaceAll(
														'{1}',
														moment
															.utc(formBodyDisplayData?.modifiedDate)
															.local()
															.locale(Utility.getLocale(userPreferences))
															.fromNow()
													)}
											</section>
											<section className="conflictDescriptionTabWrapper">
												<TabBar
													activeTab={activeConflictTab}
													tabs={[
														{id: 'current', label: labels.currentConflictVersion},
														{id: 'server', label: labels.serverConflictVersion}
													]}
													onTabChange={conflictTabChange}
													styleName={'conflictDescriptionTab'}
												/>
												{activeConflictTab === 'current' && (
													<Checkbox
														id={'conflict'}
														onChange={showConflictChanges}
														label={labels.conflictShowChanges}
														checked={conflictShowChangesChecked}
														styleName="conflictDescriptioncheckbox"
													/>
												)}
											</section>
											<section className="conflictDescriptionTxt customScrollbar">
												{Utility.createRichTextElement(conflictText)}
											</section>
										</>
									</Modal>
									<CommentInputModal />
								</>
							)}
						</GuidanceModalContext.Provider>
					</FormBodyChangesWatcher>
				)}
			</section>
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: flex;
	&.FormBodyEmptyContainer {
		height: 100%;
	}
	& > .section-body {
		border-bottom: 0 !important;
	}
	.section-container {
		display: flex;
		width: 100%;
		flex-direction: column;
		.bodyWrapper {
			&.DefaultView {
				padding: 0;
				border: 0;
				box-shadow: none;
				margin: var(--px-10) 0;
				&:first-child {
					margin-top: 0;
				}
				&:last-child {
					margin-bottom: var(--px-30);
					.RelateExistingDocumentsBodyWrapper {
						margin-bottom: 0;
					}
				}
				& + .CardView {
					margin-top: 0;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			&.CardView {
				padding: var(--px-20);
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-radius: var(--px-4);
				box-shadow: 0 var(--px-2) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin: var(--px-30) 0 var(--px-30) var(--px-10);
				&:first-child {
					margin-top: 0;
					& + .CardView {
						margin-top: 0;
					}
				}
				& + .CardView {
					margin-top: 0;
				}
				& + .DefaultView {
					margin-top: 0;
				}
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin: var(--px-10) 0 var(--px-20) var(--px-10);
				}
			}
			&.CardViewTop {
				padding: var(--px-20) var(--px-20) var(--px-10) var(--px-20);
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-width: var(--px-1) var(--px-1) 0 var(--px-1);
				border-radius: var(--px-4) var(--px-4) 0 0;
				box-shadow: 0 var(--px-2) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin-left: var(--px-10);
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			&.CardViewMiddle {
				padding: var(--px-10) var(--px-20);
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-width: 0 var(--px-1);
				box-shadow: 0 var(--px-4) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin-left: var(--px-10);
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			&.CardViewMiddleTop {
				padding: 0;
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-width: 0 var(--px-1) 0 var(--px-1);
				box-shadow: 0 var(--px-4) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin-left: var(--px-10);
				.CardViewMiddleTop {
					margin: 0 var(--px-20);
					border-top: var(--px-1) solid var(--neutrals-200);
					padding: var(--px-20) 0;
					width: calc(100% - var(--px-40));
				}
				.StyledToggleSwitchListContainer {
					margin: 0 var(--px-20);
					border-top: var(--px-1) solid var(--neutrals-200);
					padding: var(--px-20) 0;
					width: calc(100% - var(--px-40));
				}
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			&.CardViewBottom {
				padding: var(--px-10) var(--px-20) var(--px-20) var(--px-20);
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-width: 0 var(--px-1) var(--px-1) var(--px-1);
				border-radius: 0 0 var(--px-4) var(--px-4);
				box-shadow: 0 var(--px-4) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin-bottom: var(--px-30);
				margin-left: var(--px-10);
				& + .DefaultView {
					margin-top: 0;
				}
				& + .CardView {
					margin-top: 0;
				}
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			&.CardViewBottomTop {
				padding: 0;
				background-color: var(--neutrals-00white);
				border: var(--px-1) solid var(--neutrals-200);
				border-width: 0 var(--px-1) var(--px-1) var(--px-1);
				border-radius: 0 0 var(--px-4) var(--px-4);
				box-shadow: 0 var(--px-4) var(--px-4) 0 rgba(0, 0, 0, 0.5);
				margin-bottom: var(--px-30);
				margin-left: var(--px-10);
				& + .DefaultView {
					margin-top: 0;
				}
				& + .CardView {
					margin-top: 0;
				}
				.CardViewBottomTop {
					margin: 0 var(--px-20);
					border-top: var(--px-1) solid var(--neutrals-200);
					padding: var(--px-20) 0;
					width: calc(100% - var(--px-40));
				}
				.StyledToggleSwitchListContainer {
					margin: 0 var(--px-20);
					border-top: var(--px-1) solid var(--neutrals-200);
					padding: var(--px-20) 0;
					width: calc(100% - var(--px-40));
				}
				.toggleButton,
				.section-radio,
				.section-checkbox-Container,
				.MultiSelectTileBodyData .section-checkbox,
				.SingleSelectTileBodyData .section-radio {
					box-shadow: none !important;
					border-width: var(--px-1) !important;
				}
				.RiskFactorContainer {
					margin-left: 0;
				}
			}
			.SingleSelectTileBodyData {
				.SingleSelectTileBody {
					.section-radio {
						border: var(--px-2) solid var(--neutrals-300);
						background-color: var(--neutrals-00white);
						padding: var(--px-16) var(--px-20);
						border-radius: var(--px-4);
					}
				}
			}
		}
	}
	.SectionBodyWrapper {
		padding: 0;
		border: 0;
		width: 100%;
		font-size: var(--px-14);
		display: flex;
		flex-direction: column;
		&.validationBorder {
			border-left: var(--px-4) solid var(--red-600);
		}
		&.DefaultView {
			.SectionBodyDescription {
				.section-showless-header-description {
				}
			}
		}
		.NoResponseLabel,
		.NoResponseLabel span {
			font-weight: 600 !important;
			color: var(--red-600) !important;
		}
		.SectionBodyDescription {
			display: flex;
			padding: var(--px-10) 0 var(--px-10) var(--px-10);
		}
		.SectionBodyData {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);
			padding: 0 var(--px-10) 0 var(--px-10);
			margin-bottom: var(--px-10);
			margin-top: var(--px-10);
			&.greyOutArea {
				.MotifProgressBarWrapper {
					display: flex;
					.motif-progress-bar-wrapper {
						span {
							white-space: nowrap !important;
						}
					}
				}
			}
			.show-more-less {
				width: 100%;
				.k-table-resize-handle-wrapper,
				.k-row-resize-handle-wrapper {
					width: 100% !important;
				}
			}
			.diffins {
				background-color: #e6ffe6;
				font-family: inherit;
				text-decoration: none;
			}
			.diffdel {
				color: #999;
				background-color: #ffe6e6;
				font-family: inherit;
			}
			.mod {
				background-color: #ffe1ac;
				font-family: inherit;
			}
		}
		.SectionBodyDescriptionWrapper {
			display: flex;
			align-items: flex-start;
			font-weight: 300;
			color: var(--neutrals-900);
			padding: var(--px-10);
		}
	}
`;
